import {
  QueryHookOptions,
  useQuery,
  useLazyQuery,
  LazyQueryHookOptions,
} from "@apollo/client";

import {
  DashboardReportQuery,
  DashboardReportQueryVariables,
  DashboardReportDocument,
  DashboardHarvestDataQuery,
  DashboardHarvestDataQueryVariables,
  DashboardHarvestDataDocument,
  DashboardHarvestChartQuery,
  DashboardHarvestChartQueryVariables,
  DashboardHarvestChartDocument,
} from "../../graphql";

export function useDashboardReport(
  options?: QueryHookOptions<
    DashboardReportQuery,
    DashboardReportQueryVariables
  >
) {
  const { loading, data } = useQuery(DashboardReportDocument, options);

  if (!data || !data.dashboardReport) {
    return { loading };
  }

  const report = data.dashboardReport || {};

  return {
    loading,
    workOrders: {
      workOrderStatusSummary: report.workOrderStatusSummary,
      totalAwaitingInventoryWorkOrders: report.totalAwaitingInventoryWorkOrders,
      workOrderTypeSummary: report.workOrderTypeSummary,
    },
    localitySummary: {
      localitySummary: report.localitySummary,
      cropStageSummary: report.cropStageSummary,
      cropVarietySummary: report.cropVarietySummary,
    },
  };
}

export function useLazyDashboardReport(
  options?: LazyQueryHookOptions<
    DashboardReportQuery,
    DashboardReportQueryVariables
  >
) {
  const [load, { loading, data }] = useLazyQuery(
    DashboardReportDocument,
    options
  );

  const report = data?.dashboardReport;

  const normalized = report
    ? {
        workOrders: {
          workOrderStatusSummary: report.workOrderStatusSummary,
          totalAwaitingInventoryWorkOrders:
            report.totalAwaitingInventoryWorkOrders,
          workOrderTypeSummary: report.workOrderTypeSummary,
        },
        localitySummary: {
          localitySummary: report.localitySummary,
          cropStageSummary: report.cropStageSummary,
          cropVarietySummary: report.cropVarietySummary,
        },
      }
    : undefined;

  return [load, { loading, ...normalized }] as const;
}

export function useDashboardHarvestData(
  options?: QueryHookOptions<
    DashboardHarvestDataQuery,
    DashboardHarvestDataQueryVariables
  >
) {
  const { loading, data } = useQuery(DashboardHarvestDataDocument, options);

  return {
    loading,
    summary: data?.dashboardHarvestData,
  };
}

export function useDashboardHarvestChart(
  options?: QueryHookOptions<
    DashboardHarvestChartQuery,
    DashboardHarvestChartQueryVariables
  >
) {
  const { loading, data } = useQuery(DashboardHarvestChartDocument, options);

  return {
    loading,
    harvestUnit: data?.dashboardHarvestChart?.harvestUnit,
    harvestData: data?.dashboardHarvestChart?.harvestData,
  };
}
